import {AsyncPipe, isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostBinding,
  inject,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {DCP_CHAT_ADVISOR_PAGE_CONFIG} from '@app/config/chat-advisor-page.config';
import {DataTrackingService} from '@app/services/analytics/data-tracking.service';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {Sap4MeHideHeaderService} from '@app/services/sap4me-hide-header.service';
import {
  ChatAdvisorContextService,
  ChatAdvisorPageService,
  DcpChatAdvisorComponent,
  FeatureTogglesService,
  HideHeaderService,
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Country, RoutingService} from '@spartacus/core';
import {
  HamburgerMenuService,
  KeyboardFocusService,
  OutletModule,
  PageSlotModule,
  StorefrontComponent,
} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {distinctUntilChanged, filter, switchMap} from 'rxjs/operators';
import {DtrumApi} from '@dynatrace/dtrum-api-types/dtrum';
import {DcpHeaderComponent} from '@app/layout/header/component/dcp-header.component';
import {DcpGlobalMessageComponent} from '@app/global-message/global-message.component';
import {ToastsComponent} from '@app/global-message/toasts/toasts.component';
import {DcpUdexFooterComponent} from '@app/layout/footer/dcp-udex-footer/dcp-udex-footer.component';
import {DcpPageLayoutComponent} from '@app/temp/page-layout/page-layout.component';

declare global {
  interface Window {
    dtrum?: DtrumApi;
  }
}

@UntilDestroy()
@Component({
  selector: 'dcp-storefront',
  templateUrl: './dcp-storefront.component.html',
  styleUrls: ['./dcp-storefront.component.scss'],
  imports: [
    AsyncPipe,
    RouterModule,
    OutletModule,
    PageSlotModule,
    DcpPageLayoutComponent,
    DcpHeaderComponent,
    DcpGlobalMessageComponent,
    ToastsComponent,
    DcpUdexFooterComponent,
    DcpChatAdvisorComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DcpStorefrontComponent extends StorefrontComponent implements OnInit {
  @HostBinding('attr.tabindex') tabindex = null;

  @Input() isModal = false;
  hideHeader: boolean;
  countries: Country[] = [];
  addCaptchaContainer: boolean = false;
  reCaptchaContainerId: string = 'g-recaptcha-container';
  showChatAdvisor$: Observable<boolean>;
  chatAdvisorContext$ = inject(ChatAdvisorContextService).context$;
  protected readonly JSON = JSON;
  private hideHeaderService: HideHeaderService = inject(HideHeaderService);
  private chatAdvisorPageService: ChatAdvisorPageService = inject(ChatAdvisorPageService);

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    public countrySelectorService: CountrySelectorService,
    hamburgerMenuService: HamburgerMenuService,
    routingService: RoutingService,
    elementRef: ElementRef,
    keyboardFocusService: KeyboardFocusService,
    protected sap4meHideHeaderService: Sap4MeHideHeaderService,
    private dataTrackingService: DataTrackingService,
    private togglesService: FeatureTogglesService,
    private cdr: ChangeDetectorRef,
    protected router: Router,
  ) {
    super(hamburgerMenuService, routingService, elementRef, keyboardFocusService);
  }

  ngOnInit(): void {
    this.setupTracking();
    this.embedYoutubeIframeApi();
    this.chatAdvisorPageService.init(DCP_CHAT_ADVISOR_PAGE_CONFIG);
    this.showChatAdvisor$ = this.chatAdvisorPageService.showChatAdvisor$;
    super.ngOnInit();
    this.subscribeToCountries();
    this.updateHideHeader(this.hideHeaderService.getHideStatus());
    if (window.self !== window.top) {
      this.checkIfShouldHideHeaderInIframe();
    }
    this.displayHeader();
    this.checkReCaptchaState();
  }

  @HostBinding('class.pt-0') get headerStatus() {
    return this.hideHeader;
  }

  @HostBinding('class.header-navigation-wrapper') applyWrapper = true;

  displayHeader(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.sap4meHideHeaderService
        .getHideStatus$()
        .pipe(untilDestroyed(this))
        .subscribe((hideHeader) => {
          // Override for checkout and order confirmation routes in iframe
          if (window.self !== window.top && this.shouldShowHeaderInIframe(this.router.url)) {
            this.updateHideHeader(false);
            return;
          }
          this.updateHideHeader(hideHeader);
        });
    }
  }

  updateHideHeader(hideHeader: boolean): void {
    this.hideHeader = hideHeader;
    this.elementRef.nativeElement.style.paddingTop = this.hideHeader ? '0px' : '';
    this.cdr.detectChanges();
  }

  embedYoutubeIframeApi() {
    if (isPlatformBrowser(this.platformId)) {
      const tag: HTMLElementTagNameMap['script'] = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }
  }

  setupTracking() {
    if (isPlatformBrowser(this.platformId)) {
      this.dataTrackingService.setupPageHitTracking();
      this.dataTrackingService.setupProductDataTracking();
    }
  }

  checkReCaptchaState() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationEnd),
          filter((e: NavigationEnd) => e.url.includes('/product')),
          switchMap(() => {
            return this.togglesService.isEnabled(UNLEASH_TOGGLES.leadFormCaptcha).pipe(untilDestroyed(this));
          }),
          untilDestroyed(this),
        )
        .subscribe((captchaEnable: boolean) => {
          this.addCaptchaContainer = captchaEnable;
        });
    }
  }

  private subscribeToCountries() {
    this.countrySelectorService
      .getCountries()
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((countries) => {
        this.countries = countries;
      });
  }

  private checkIfShouldHideHeaderInIframe(): void {
    // Set initial header visibility based on current route
    this.updateIframeHeaderVisibility(this.router.url);

    // Update header visibility when navigating to different routes
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe((event: NavigationEnd) => {
        this.updateIframeHeaderVisibility(event.url);
      });
  }

  private updateIframeHeaderVisibility(url: string): void {
    const shouldShowHeader = this.shouldShowHeaderInIframe(url);
    this.isModal = !shouldShowHeader;

    if (shouldShowHeader) {
      this.sap4meHideHeaderService.revertHiding();
    } else {
      this.sap4meHideHeaderService.hideHeader();
    }
  }

  private shouldShowHeaderInIframe(url: string): boolean {
    return url.includes('/checkout') || url.includes('/order-confirmation');
  }
}
